import { WorkflowVersion } from "@/components/Workflows/types";
import { ToastType } from "@/enums";
import { ListPromptVersion } from "@/types";
import { displayToast, displayErrorToast } from "@/utils/toast";
import { DropResult } from "@hello-pangea/dnd";
import { useState, useEffect } from "react";
import { Label } from "../VersionTab/types";

const useLabelDragAndDrop = (
  versions: (WorkflowVersion | ListPromptVersion)[] | undefined,
  onMoveReleaseLabel: (
    labelId: number,
    labelName: string,
    targetVersionId: number,
  ) => Promise<unknown>,
) => {
  const [isDragging, setIsDragging] = useState(false);
  const [pendingLabelMoves, setPendingLabelMoves] = useState<{
    [key: string]: {
      sourceVersionId: number;
      targetVersionId: number;
      label: Label;
    };
  }>({});
  const [labelMoveConfirmation, setLabelMoveConfirmation] = useState<{
    isOpen: boolean;
    labelId: number | null;
    labelName: string;
    sourceVersionId: number | null;
    targetVersionId: number | null;
  }>({
    isOpen: false,
    labelId: null,
    labelName: "",
    sourceVersionId: null,
    targetVersionId: null,
  });

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = async (result: DropResult) => {
    setIsDragging(false);

    if (!result.destination) {
      return;
    }

    const { draggableId, source, destination } = result;

    if (source.droppableId === destination.droppableId) {
      return;
    }

    const labelName = draggableId.split("-")[2];
    const labelId = parseInt(draggableId.split("-")[1]);

    const sourceVersionId = parseInt(
      source.droppableId.replace("version-", ""),
    );
    const targetVersionId = parseInt(
      destination.droppableId.replace("version-", ""),
    );

    let sourceVersion;

    const pendingMoveKey = Object.keys(pendingLabelMoves).find((key) => {
      const move = pendingLabelMoves[key];
      return move.label.id === labelId;
    });

    if (pendingMoveKey) {
      sourceVersion = versions?.find((v) => v.id === sourceVersionId);
    } else {
      sourceVersion = versions?.find((v) =>
        v.labels.some((l) => l.id === labelId),
      );
    }

    const movedLabel =
      sourceVersion?.labels.find((l) => l.id === labelId) ||
      (pendingMoveKey ? pendingLabelMoves[pendingMoveKey].label : null);

    if (!movedLabel) return;

    setLabelMoveConfirmation({
      isOpen: true,
      labelId,
      labelName,
      sourceVersionId,
      targetVersionId,
    });
  };

  const executeReleaseMove = async (): Promise<void> => {
    if (
      !labelMoveConfirmation.labelId ||
      !labelMoveConfirmation.targetVersionId ||
      !labelMoveConfirmation.sourceVersionId
    )
      return;

    const { labelId, labelName, targetVersionId, sourceVersionId } =
      labelMoveConfirmation;

    const labelMoveKey = `${labelId}-${targetVersionId}`;

    let sourceVersion;
    const pendingMoveKey = Object.keys(pendingLabelMoves).find((key) => {
      const move = pendingLabelMoves[key];
      return move.label.id === labelId;
    });

    if (pendingMoveKey) {
      sourceVersion = versions?.find((v) => v.id === sourceVersionId);

      setPendingLabelMoves((prev) => {
        const updated = { ...prev };
        delete updated[pendingMoveKey];
        return updated;
      });
    } else {
      sourceVersion = versions?.find((v) =>
        v.labels.some((l) => l.id === labelId),
      );
    }

    const movedLabel =
      sourceVersion?.labels.find((l) => l.id === labelId) ||
      (pendingMoveKey ? pendingLabelMoves[pendingMoveKey].label : null);

    if (!movedLabel) return;

    setPendingLabelMoves((prev) => ({
      ...prev,
      [labelMoveKey]: {
        sourceVersionId,
        targetVersionId,
        label: movedLabel,
      },
    }));

    setLabelMoveConfirmation((prev) => ({ ...prev, isOpen: false }));

    try {
      await onMoveReleaseLabel(labelId, labelName, targetVersionId);
      displayToast("Release label moved successfully", ToastType.success);
    } catch (error) {
      console.error("Error moving label:", error);
      displayErrorToast("Failed to move release label");

      setPendingLabelMoves((prev) => {
        const updated = { ...prev };
        delete updated[labelMoveKey];
        return updated;
      });
    }
  };

  useEffect(() => {
    if (Object.keys(pendingLabelMoves).length === 0 || !versions) return;

    const updatedPendingMoves = { ...pendingLabelMoves };
    let hasChanges = false;

    Object.entries(pendingLabelMoves).forEach(([labelKey, moveInfo]) => {
      const { targetVersionId, label } = moveInfo;

      const targetVersion = versions.find((v) => v.id === targetVersionId);

      if (
        targetVersion &&
        targetVersion.labels.some((l) => l.id === label.id)
      ) {
        delete updatedPendingMoves[labelKey];
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setPendingLabelMoves(updatedPendingMoves);
    }
  }, [versions, pendingLabelMoves]);

  return {
    isDragging,
    labelMoveConfirmation,
    setLabelMoveConfirmation,
    handleDragStart,
    handleDragEnd,
    executeReleaseMove,
    pendingLabelMoves,
  };
};

export default useLabelDragAndDrop;
